import { useMemo } from "react";

import { endOfDay, formatISODateTimeString, isSameDay, parseFloatTime, startOfDay } from "@/config/date-fns";
import { design } from "@/config/design";
import { EntryFragment } from "@/generated/graphql/graphql";
import { useDimensionStore } from "@/stores/dimensionStore";

import { ClockPieChartData } from "./ClockPieChartData";
import { ClockPieChartHand } from "./ClockPieChartHand";
import { ClockPieChartLayout } from "./ClockPieChartLayout";
import { ClockPieChartSvg } from "./ClockPieChartSvg";
import { SquareBlock } from "./SquareBlock";

export type EntrySegment = EntryFragment & {
  startHour: number;
  endHour: number;
};

type ClockPieChartProps = {
  entries: EntryFragment[] | null | undefined;
  isDisplayHand?: boolean;
  baseDate: Date;
};

export function ClockPieChart({ isDisplayHand, ...props }: ClockPieChartProps) {
  const { dimensionType } = useDimensionStore();

  const entrySegments = useMemo<EntrySegment[]>(() => {
    const entries = props.entries || [];

    return entries.map((entry) => {
      const startAt = isSameDay(entry.startAt, props.baseDate)
        ? entry.startAt
        : formatISODateTimeString(startOfDay(props.baseDate));
      const endAt = entry.endAt
        ? isSameDay(entry.endAt, props.baseDate)
          ? entry.endAt
          : formatISODateTimeString(endOfDay(props.baseDate))
        : formatISODateTimeString(new Date());

      return {
        ...entry,
        endAt,
        endHour: parseFloatTime(endAt),
        startAt,
        startHour: parseFloatTime(startAt),
      };
    });
  }, [props.baseDate, props.entries]);

  return (
    <SquareBlock>
      <ClockPieChartSvg viewBox="0 0 500 500">
        <g transform={`translate(${design.ClockPieChart.WIDTH / 2}, ${design.ClockPieChart.HEIGHT / 2})`}>
          <ClockPieChartData
            dimensionType={dimensionType}
            entrySegments={entrySegments}
          />
          <ClockPieChartLayout entrySegments={entrySegments} />
          {isDisplayHand && <ClockPieChartHand />}
        </g>
      </ClockPieChartSvg>
    </SquareBlock>
  );
}
